import {AnimatePresence, motion} from 'motion/react';
import {useState} from 'react';
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {APP_ANIMATIONS} from '@/const';
import EmailIcon from '@/icons/EmailIcon';
import useHandleBackendErrors from '@/hooks/useHandleBackendErrors';
import useRetrievePassword from '@/queries/auth/useRetrievePassword';
import Button, {BTN_TYPES} from '@/ui/Button';
import Input from '@/ui/Input';
import RelatedAdsLogoMedia from '@/ui/RelatedAdsLogoMedia';
import {AuthSteps, ILoginFormsProps} from '../../interface';
import AdditionalAction from '../AdditionalAction';
import ResponseMessage from '../ResponseMessage';
import StepTitle from '../StepTitle';
import styles from './styles.module.scss';

interface IFormValues {
    user_login: string;
}

const ForgotForm = ({setStep = () => {}}: ILoginFormsProps) => {
    const {mutate, isPending, error} = useRetrievePassword();
    const formMethods = useForm<IFormValues>({values: {user_login: ''}});
    const [done, setDone] = useState(false);

    const {
        handleSubmit,
        register,
        setError,
        formState: {errors}
    } = formMethods;

    const onSubmit: SubmitHandler<IFormValues> = data => mutate({data}, {onSuccess: () => setDone(true)});

    const backToLogin = () => setStep(AuthSteps.login);

    useHandleBackendErrors({error, setError});

    return (
        <FormProvider {...formMethods}>
            <RelatedAdsLogoMedia />
            <AnimatePresence mode={'wait'}>
                {done ? (
                    <motion.div key={'done'} {...APP_ANIMATIONS.fadeInOut} className={styles.form}>
                        <ResponseMessage
                            message={'Your new password has been sent to your email address'}
                            messageStyles={[styles.message]}
                        />
                        <div className={styles.bottom}>
                            <Button btnStyles={[styles.submit]} onClick={backToLogin}>
                                Log in
                            </Button>
                            <AdditionalAction
                                text={'Don’t receive an e-mail?'}
                                btnText={'Resend'}
                                onClick={() => setDone(false)}
                            />
                        </div>
                    </motion.div>
                ) : (
                    <motion.form
                        key={'form'}
                        {...APP_ANIMATIONS.fadeInOut}
                        onSubmit={handleSubmit(onSubmit)}
                        className={styles.form}
                        noValidate
                    >
                        <div className={styles.titles}>
                            <StepTitle title={'Forgot password'} />
                            <div>Don’t worry, we’ll send you reset instructions</div>
                        </div>
                        <Input
                            error={errors?.user_login?.message}
                            Icon={EmailIcon}
                            inputProps={{
                                placeholder: 'E-mail',
                                type: 'email',
                                ...register('user_login', {
                                    required: 'E-mail is required',
                                    validate: {
                                        matchPattern: v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'E-mail is invalid'
                                    }
                                })
                            }}
                        />
                        <Button loading={isPending} btnStyles={[styles.submit]}>
                            Reset password
                        </Button>
                    </motion.form>
                )}
            </AnimatePresence>
            <Button
                btnType={BTN_TYPES.opacity}
                onClick={done ? () => setDone(false) : backToLogin}
                btnStyles={[styles.backBtn]}
            >
                Back
            </Button>
        </FormProvider>
    );
};

export default ForgotForm;
